import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'

import { DATA_CHANGE_REQUEST_TYPES, type DataChangeRequestType } from 'constants/data-change-requests'
import { useAddNotification } from 'hooks/useAddNotification'
import {
  type DataChangeRequestRow,
  dataChangeRequestFromServer,
  getDataChangeRequestQuery,
} from 'models/data-change-request-model'
import { get, type Paginated, post, put } from 'utils/http'

const CUSTOMER_ID = 6
const LIST_URL = '/management/customers/companies/data_change_requests'

// List keyed on the whole meta; items mapped to the camelCase working shape.
export const useDataChangeRequests = (meta: any) =>
  useQuery({
    queryKey: ['dataChangeRequests', meta],
    queryFn: async (): Promise<Paginated<DataChangeRequestRow>> => {
      const { count, items } = await get<Paginated<any>>(LIST_URL, { query: getDataChangeRequestQuery(meta) })
      return { count, items: (items || []).map(dataChangeRequestFromServer) }
    },
  })

// The list count is already type-filtered, so ask for one row and keep the count.
export const useDataChangeRequestTypeCounts = (filters: Record<string, any>) => {
  const results = useQueries({
    queries: DATA_CHANGE_REQUEST_TYPES.map(({ value: type }) => ({
      queryKey: ['dataChangeRequests', 'count', type, filters?.status, filters?.search],
      queryFn: async () => {
        const { count } = await get<Paginated<unknown>>(LIST_URL, {
          query: {
            limit: 1,
            offset: 0,
            type,
            statuses: filters?.status || undefined,
            search: filters?.search || undefined,
          },
        })
        return count
      },
    })),
  })

  return Object.fromEntries(DATA_CHANGE_REQUEST_TYPES.map(({ value }, i) => [value, results[i]?.data])) as Partial<
    Record<DataChangeRequestType, number>
  >
}

// Single change request (keyed distinctly from the list).
export const useDataChangeRequest = (id: any) =>
  useQuery({
    queryKey: ['dataChangeRequest', id],
    queryFn: async () => dataChangeRequestFromServer(await get(`${LIST_URL}/${id}`)),
    enabled: id != null,
  })

// Pending requests for one company (the company page's "go to pending request" action).
export const usePendingCompanyDataChangeRequests = (uuid: any) =>
  useQuery({
    queryKey: ['dataChangeRequests', 'pending', uuid],
    queryFn: async () => {
      const { items } = await get<Paginated<any>>(LIST_URL, {
        query: { search: uuid, statuses: 'pending', limit: 25, offset: 0 },
      })
      return (items || []).map(dataChangeRequestFromServer).filter(x => x.uuid === uuid && x.status === 'pending')
    },
    enabled: !!uuid,
    initialData: [] as DataChangeRequestRow[],
  })

const invalidateAll = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ['dataChangeRequests'] })
  queryClient.invalidateQueries({ queryKey: ['dataChangeRequest'] })
}

// Accept/reject notify the outcome and always invalidate (refresh the list) even on failure, so the caller's post-await cleanup still runs.
const requestCount = (ids: any[]) => `${ids.length} change request${ids.length === 1 ? '' : 's'}`

const acceptRequests = (ids: number[]) => post('/management/companies/data_change_requests/accepted', ids)

export const useAcceptDataChangeRequests = () => {
  const queryClient = useQueryClient()
  const addNotification = useAddNotification()
  return useMutation({
    mutationFn: async (ids: any[]) => {
      try {
        await acceptRequests(ids)
        addNotification(`Accepted ${requestCount(ids)}`, 'success')
      } catch {
        addNotification(`Failed to accept ${requestCount(ids)}`, 'error')
      }
      invalidateAll(queryClient)
    },
  })
}

export const useRejectDataChangeRequests = () => {
  const queryClient = useQueryClient()
  const addNotification = useAddNotification()
  return useMutation({
    mutationFn: async (ids: any[]) => {
      try {
        await post('/management/companies/data_change_requests/rejected', ids)
        addNotification(`Rejected ${requestCount(ids)}`, 'success')
      } catch {
        addNotification(`Failed to reject ${requestCount(ids)}`, 'error')
      }
      invalidateAll(queryClient)
    },
  })
}

export const useUpdateDataChangeRequest = () => {
  const queryClient = useQueryClient()
  const addNotification = useAddNotification()
  return useMutation({
    mutationFn: async ({ id, payload }: any) => {
      try {
        await put(`${LIST_URL}/${id}`, payload)
        addNotification('Change request updated', 'success')
        queryClient.invalidateQueries({ queryKey: ['dataChangeRequest', id] })
        return true
      } catch {
        addNotification('Failed to update change request', 'error')
        return false
      }
    },
  })
}

// Shared create helper; count-based plural messages preserved verbatim from the old thunk.
// Resolves to the created ids, or null when the create itself failed (already notified).
const createDataChangeRequest = async ({ companyUuid, type, data, addNotification }: any) => {
  const count = Array.isArray(companyUuid) ? companyUuid.length : 1
  const plural = count > 1 ? 's' : ''
  try {
    const { ids } = await post<{ ids: number[] }>(`/customers/${CUSTOMER_ID}/companies/data_change_requests`, {
      company_uuid: companyUuid,
      type,
      data,
    })
    addNotification(`Succesfully created ${count} change request${plural}`, 'success')
    return ids ?? []
  } catch {
    addNotification(`Failed to create ${count} data change request${plural}`, 'error')
    return null
  }
}

// RequestChange (type 'lead', full proposed data).
export const useCreateCompanyDataChangeRequest = () => {
  const queryClient = useQueryClient()
  const addNotification = useAddNotification()
  return useMutation({
    mutationFn: async ({ companyUuids, data }: any) => {
      const ids = await createDataChangeRequest({ companyUuid: companyUuids, type: 'lead', data, addNotification })
      invalidateAll(queryClient)
      return ids
    },
  })
}

// MarkAsIsp — create an ISP request then immediately accept it.
export const useMarkCompanyAsIsp = () => {
  const queryClient = useQueryClient()
  const addNotification = useAddNotification()
  return useMutation({
    mutationFn: async (companyUuid: any) => {
      const ids = await createDataChangeRequest({ companyUuid, type: 'isp', addNotification })
      if (ids) {
        // no ids means the request was created but cannot be accepted from here, so it stays pending
        const accepted =
          ids.length > 0 &&
          (await acceptRequests(ids)
            .then(() => true)
            .catch(() => false))
        if (!accepted) addNotification('Created the ISP request but could not accept it', 'error')
      }
      invalidateAll(queryClient)
    },
  })
}
